import { useContext } from "react";
import {
  TextField,
  Autocomplete,
  Typography,
  InputAdornment,
  Box,
  Divider,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import EditIcon from "@mui/icons-material/Edit";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import moment, { Moment } from "moment";
import { CacheContext } from "../../Context/cacheContext";
import { Vocabulary } from "../../Utils/Vocabulary";
import { isMobile } from "../../Utils/utils";
import { BNRExchangeRates } from "../../Utils/Constants";
import { NumericFormatCustom } from "../../Utils/NumericFormatComponent";

export type AntecontractFieldsData = {
  firma: { id: string | number; nume_firma: string } | null;
  advanceInvoiceNumber: string;
  advancePaymentDate: Moment | null;
  carPrice: string;
  advanceValue: string;
  remainingPayment: string;
  exchangeRate: string;
  vehicleId: string;
  vehicleName: string;
  VIN: string;
};

type AntecontractFieldsProps = {
  data: AntecontractFieldsData;
  onChange: (field: keyof AntecontractFieldsData, value: unknown) => void;
  onOpenVehicleSearch: () => void;
  showFirma?: boolean;
  errors?: Partial<Record<keyof AntecontractFieldsData, string>>;
};

export default function AntecontractFields({
  data,
  onChange,
  onOpenVehicleSearch,
  showFirma = true,
  errors = {},
}: AntecontractFieldsProps) {
  const cacheContext = useContext(CacheContext);

  const sectionStyle = {
    backgroundColor: "#fafafa",
    borderRadius: 2,
    padding: 2,
    marginTop: 2,
  };

  const labelStyle = {
    fontSize: 13,
    fontWeight: 600,
    color: "#666",
    marginBottom: 1.5,
    textTransform: "uppercase" as const,
    letterSpacing: 0.5,
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {showFirma && (
        <Autocomplete
          freeSolo={false}
          style={{ width: "100%" }}
          size="small"
          disablePortal
          value={data.firma}
          onChange={(_, value) => onChange("firma", value)}
          getOptionLabel={(option) => option?.nume_firma || ""}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          options={cacheContext?.cache?.company || []}
          renderInput={(params) => (
            <TextField
              {...params}
              label={Vocabulary.nume_firma}
              error={!!errors.firma}
              helperText={errors.firma}
              required
            />
          )}
        />
      )}

      {/* Invoice Section */}
      <Box sx={sectionStyle}>
        <Typography sx={labelStyle}>{Vocabulary.invoiceData}</Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile() ? "column" : "row",
            gap: 2,
          }}
        >
          <TextField
            size="small"
            fullWidth
            label={Vocabulary.advanceInvoiceNumber}
            value={data.advanceInvoiceNumber}
            onChange={(e) => onChange("advanceInvoiceNumber", e.target.value)}
            error={!!errors.advanceInvoiceNumber}
            helperText={errors.advanceInvoiceNumber}
          />
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              label={Vocabulary.payedDate}
              value={data.advancePaymentDate ? moment(data.advancePaymentDate) : null}
              onChange={(date) => onChange("advancePaymentDate", date)}
              maxDate={moment()}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  error: !!errors.advancePaymentDate,
                  helperText: errors.advancePaymentDate,
                  inputProps: { readOnly: true },
                },
              }}
            />
          </LocalizationProvider>
        </Box>
      </Box>

      {/* Pricing Section */}
      <Box sx={sectionStyle}>
        <Typography sx={labelStyle}>{Vocabulary.sellingData}</Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile() ? "column" : "row",
            gap: 2,
          }}
        >
          <TextField
            size="small"
            fullWidth
            label={Vocabulary.sellPrice}
            value={data.carPrice}
            onChange={(e) => onChange("carPrice", e.target.value)}
            error={!!errors.carPrice}
            helperText={errors.carPrice}
            InputProps={{
              inputComponent: NumericFormatCustom as any,
              endAdornment: <InputAdornment position="end">{Vocabulary.currency}</InputAdornment>,
            }}
          />
          <TextField
            size="small"
            fullWidth
            label={Vocabulary.advance}
            value={data.advanceValue}
            onChange={(e) => onChange("advanceValue", e.target.value)}
            error={!!errors.advanceValue}
            helperText={errors.advanceValue}
            InputProps={{
              inputComponent: NumericFormatCustom as any,
              endAdornment: <InputAdornment position="end">{Vocabulary.currency}</InputAdornment>,
            }}
          />
          <TextField
            size="small"
            fullWidth
            label={Vocabulary.restOfPayment}
            value={(parseFloat(data.carPrice) || 0) - (parseFloat(data.advanceValue) || 0)}
            error={!!errors.remainingPayment}
            helperText={errors.remainingPayment}
            disabled
            InputProps={{
              inputComponent: NumericFormatCustom as any,
              endAdornment: <InputAdornment position="end">{Vocabulary.currency}</InputAdornment>,
            }}
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#FF6633",
                fontWeight: 600,
              },
            }}
          />
        </Box>

        <Box sx={{ marginTop: 2 }}>
          <Autocomplete
            freeSolo={false}
            size="small"
            disablePortal
            value={data.exchangeRate || null}
            onChange={(_, value) => onChange("exchangeRate", value || "")}
            options={BNRExchangeRates}
            renderInput={(params) => (
              <TextField
                {...params}
                label={Vocabulary.cursBNR}
                error={!!errors.exchangeRate}
                helperText={errors.exchangeRate}
              />
            )}
          />
        </Box>
      </Box>

      <Divider sx={{ my: 1 }} />

      {/* Vehicle Section */}
      {data.vehicleId ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            padding: 2,
            backgroundColor: "#FFF0EB",
            borderRadius: 2,
            border: "1px solid #FFAA99",
          }}
        >
          <DirectionsCarIcon sx={{ color: "#FF6633", mr: 1.5, fontSize: 28 }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 600, color: "#FF6633" }}>
              {data.vehicleName}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#666" }}>
              VIN: {data.VIN}
            </Typography>
          </Box>
          <EditIcon
            sx={{
              cursor: "pointer",
              color: "#FF6633",
              "&:hover": { color: "#E5552A" },
            }}
            onClick={onOpenVehicleSearch}
          />
        </Box>
      ) : (
        <Box
          onClick={onOpenVehicleSearch}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            padding: 2.5,
            color: "#FF6633",
            fontSize: 16,
            fontWeight: 500,
            cursor: "pointer",
            textAlign: "center",
            border: "2px dashed #FFAA99",
            borderRadius: 2,
            backgroundColor: "#f5f5f5",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "#FFF0EB",
              borderColor: "#FF6633",
            },
          }}
        >
          <DirectionsCarIcon />
          {Vocabulary.associateWithVehicle}
        </Box>
      )}
    </Box>
  );
}
